import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const AIFormFillDialog = ({ template, onFillComplete, context = {} }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleAIFill = async () => {
    setLoading(true);
    try {
      const response = await api.post('/forms/ai-fill', {
        template_id: template.template_id,
        context: context
      });

      toast.success('Form filled with AI! Review and submit.');
      onFillComplete(response.data.filled_data);
      setOpen(false);
    } catch (error) {
      console.error('AI Fill Error:', error);
      toast.error('AI form fill failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        className="bg-gradient-to-r from-[#C9A961] to-[#D4C278] text-black hover:from-[#D4C278] hover:to-[#C9A961] font-semibold"
      >
        <Sparkles className="h-4 w-4 mr-2" />
        AI Fill Form
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-dark-card border-[#C9A961]/30">
          <DialogHeader>
            <DialogTitle className="text-2xl text-[#C9A961] flex items-center gap-2">
              <Sparkles className="h-6 w-6" />
              AI Form Fill
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="bg-black/40 p-4 rounded-lg border border-[#C9A961]/30">
              <p className="text-white mb-2">
                <strong>Form:</strong> {template.template_name}
              </p>
              <p className="text-gray-400 text-sm">
                AI will intelligently fill this form based on your profile, current context, and historical data.
              </p>
            </div>

            <div className="bg-gradient-to-r from-[#C9A961]/10 to-transparent p-4 rounded-lg border-l-4 border-[#C9A961]">
              <p className="text-[#C9A961] font-semibold mb-2">✨ AI will analyze:</p>
              <ul className="text-gray-300 text-sm space-y-1 ml-4">
                <li>• Your user profile and role</li>
                <li>• Current project/task context</li>
                <li>• Previous form submissions</li>
                <li>• Related data from the system</li>
              </ul>
            </div>

            <div className="flex gap-3 justify-end pt-4">
              <Button
                variant="outline"
                onClick={() => setOpen(false)}
                className="border-[#C9A961]/30 text-gray-300"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAIFill}
                disabled={loading}
                className="bg-[#C9A961] text-black hover:bg-[#D4C278]"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    AI Processing...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Fill with AI
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AIFormFillDialog;
